// src/redux/slice/reportSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { adminService } from "@/api/services/adminService";

// Define the permission type (adjust according to your actual structure)
interface Permission {
  id: string;
  name: string;
  [key: string]: any; // Add more fields if needed
}

interface ReportState {
  permissions: Permission[];
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: ReportState = {
  permissions: [],
  loading: false,
  error: null,
};

// Async thunk to fetch permissions by role
export const fetchReportPermissionsByRole = createAsyncThunk<
  Permission[], // Return type
  string,       // Argument type (roleId)
  { rejectValue: string } // Rejection error type
>("report/fetchPermissionsByRole", async (roleId, { rejectWithValue }) => {
  try {
    const response = await adminService.reportroleByIdNew(roleId);
    return response.data.permissions;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch permissions");
  }
});

// Create slice
const reportFeatureSlice = createSlice({
  name: "report",
  initialState,
  reducers: {resetReportFeatureState: () => initialState,},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportPermissionsByRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchReportPermissionsByRole.fulfilled,
        (state, action: PayloadAction<Permission[]>) => {
          state.permissions = action.payload;
          state.loading = false;
        }
      )
      .addCase(
        fetchReportPermissionsByRole.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.payload ?? "Failed to fetch permissions";
        }
      );
  },
});

export const { resetReportFeatureState } = reportFeatureSlice.actions;

export default reportFeatureSlice.reducer;
